import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import {
  Phone, MessageSquare, ChevronRight, CheckCircle2, Circle,
  Instagram, Facebook, Mail, Sparkles, Inbox, Check,
} from 'lucide-react';
import { tplMeta, chMeta } from './templateMeta';
import { DraftReply } from './DraftReply';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// Circular icon badge — one per channel/type — vivid neon
function TypeBadge({ item }) {
  if (item.type === 'call') {
    return (
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-500/25 text-blue-300">
        <Phone className="w-4 h-4" />
      </div>
    );
  }
  if (item.type === 'inquiry') {
    return (
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-violet-500/25 text-violet-300">
        <Sparkles className="w-4 h-4" />
      </div>
    );
  }
  if (item.type === 'inbound') {
    const ch = (item.channel || '').toLowerCase();
    if (ch === 'instagram') return <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-fuchsia-500/25 text-fuchsia-300"><Instagram className="w-4 h-4" /></div>;
    if (ch === 'facebook' || ch === 'messenger') return <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-500/25 text-blue-300"><Facebook className="w-4 h-4" /></div>;
    if (ch === 'whatsapp') return <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500/25 text-emerald-300"><MessageSquare className="w-4 h-4" /></div>;
    if (ch === 'email') return <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-violet-500/25 text-violet-300"><Mail className="w-4 h-4" /></div>;
    return <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-fuchsia-500/25 text-fuchsia-300"><MessageSquare className="w-4 h-4" /></div>;
  }
  // outbound text
  return (
    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500/25 text-emerald-300">
      <MessageSquare className="w-4 h-4" />
    </div>
  );
}

// Vivid channel/template pill
function KindPill({ item }) {
  if (item.type === 'call') {
    return <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md font-black bg-blue-500/30 text-blue-200" style={FONT_B}>Call</span>;
  }
  if (item.type === 'inquiry') {
    return <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md font-black bg-violet-500/30 text-violet-200" style={FONT_B}>New Inquiry</span>;
  }
  if (item.type === 'inbound') {
    const m = chMeta(item.channel);
    return <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md font-black ${m.bg} ${m.text}`} style={FONT_B}>{m.label} DM</span>;
  }
  const m = tplMeta(item.template);
  return <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md font-black ${m.bg} ${m.text}`} style={FONT_B}>{m.label}</span>;
}

function ActivityRow({ item, needs, expanded, onToggle, onOpen, onDraft, onHandled }) {
  const body = item.type === 'call' ? item.transcript : item.message;
  const canRespond = !!item.proposal_id;
  return (
    <div
      className={`bg-[#111111] border rounded-2xl p-5 cursor-pointer active:scale-[0.99] transition-transform ${needs ? 'border-violet-500/40 shadow-[0_0_0_1px_rgba(139,92,246,0.15)]' : 'border-white/5'}`}
      onClick={() => onToggle(item.id)}
      data-testid={`inbox-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3.5 min-w-0 flex-1">
          <TypeBadge item={item} />
          <div className="min-w-0 flex-1">
            <p className="text-white text-[15px] font-bold tracking-tight truncate" style={FONT_H}>
              {item.client_name || item.to || 'Unknown'}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <KindPill item={item} />
              <span className="text-zinc-500 text-[11px] tabular-nums" style={FONT_B}>{when(item.created_at)}</span>
              {item.type === 'text' && (
                item.sent
                  ? <span className="flex items-center gap-1 text-emerald-400 text-[11px] font-semibold" style={FONT_B}><CheckCircle2 className="w-3 h-3" />sent</span>
                  : <span className="flex items-center gap-1 text-zinc-500 text-[11px] font-semibold" style={FONT_B}><Circle className="w-3 h-3" />queued</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {canRespond && (
            <button
              onClick={() => onDraft(item)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[12px] font-bold active:scale-95 transition-transform ${needs ? 'bg-white text-black' : 'bg-white/10 border border-white/15 text-zinc-200'}`}
              style={FONT_B}
              data-testid={`thread-respond-${item.proposal_id}`}
            >
              <Sparkles className="w-3.5 h-3.5" /> Respond
            </button>
          )}
          {needs ? (
            <button
              onClick={() => onHandled(item.proposal_id)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-zinc-400 hover:text-emerald-400 active:scale-95 transition-transform"
              data-testid={`thread-done-${item.proposal_id}`}
              aria-label="Mark responded"
            >
              <Check className="w-4 h-4" />
            </button>
          ) : (
            item.proposal_id && (
              <button
                onClick={() => onOpen(item.proposal_id)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 transition-transform"
                data-testid={`inbox-open-${item.id}`}
                aria-label="Open lead"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      </div>
      {body && (
        <p
          className={`text-zinc-300 text-[13px] leading-relaxed mt-3 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}
          style={FONT_B}
        >
          {body}
        </p>
      )}
    </div>
  );
}

const Label = ({ children, count, tone, dotCls }) => (
  <div className="flex items-baseline gap-3 mb-4">
    {dotCls && <span className={`w-2 h-2 rounded-full ${dotCls}`} />}
    <h3 className={`text-[24px] font-black tracking-tighter leading-none ${tone || 'text-white'}`} style={FONT_H}>{children}</h3>
    {count > 0 && (
      <span className={`text-sm font-black tabular-nums ${tone || 'text-zinc-500'} opacity-70`} style={FONT_H}>{count}</span>
    )}
  </div>
);

export const InboxView = ({ onOpenLead }) => {
  const [items, setItems] = useState(null);
  const [threads, setThreads] = useState([]);
  const [needsIds, setNeedsIds] = useState(new Set());
  const [threadPhones, setThreadPhones] = useState({});
  const [threadDmFlag, setThreadDmFlag] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [draftFor, setDraftFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/activity');
      setItems(data.items || []);
      setThreads(data.threads || []);
      const nSet = new Set();
      const phoneMap = {};
      const dmMap = {};
      (data.threads || []).forEach(t => {
        if (t.needs_response && t.proposal_id) nSet.add(t.proposal_id);
        if (t.proposal_id) {
          phoneMap[t.proposal_id] = t.client_phone;
          dmMap[t.proposal_id] = t.preview?.type === 'inbound';
        }
      });
      setNeedsIds(nSet);
      setThreadPhones(phoneMap);
      setThreadDmFlag(dmMap);
    } catch (err) {
      console.error('Failed to load inbox', err);
      toast.error('Failed to load inbox');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Priorities: build one row per needs-response thread.
  // Prefer the most recent activity item for that lead; fall back to a synthetic
  // "inquiry" row from thread preview when there's no activity yet.
  const { priorityItems, feedItems } = useMemo(() => {
    if (!items) return { priorityItems: [], feedItems: [] };
    const itemsByPid = new Map();
    for (const it of items) {
      if (it.proposal_id && !itemsByPid.has(it.proposal_id)) itemsByPid.set(it.proposal_id, it);
    }
    const pri = [];
    for (const th of threads) {
      if (!th.needs_response || !th.proposal_id) continue;
      const existing = itemsByPid.get(th.proposal_id);
      if (existing) {
        pri.push(existing);
      } else {
        // synthetic inquiry row
        pri.push({
          type: th.preview?.type === 'inbound' ? 'inbound' : 'inquiry',
          id: `inquiry-${th.proposal_id}`,
          proposal_id: th.proposal_id,
          client_name: th.client_name,
          to: th.client_phone,
          channel: th.preview?.channel,
          template: null,
          message: th.preview?.snippet,
          created_at: th.last_activity || th.preview?.created_at,
        });
      }
    }
    return { priorityItems: pri, feedItems: items };
  }, [items, threads]);

  const openDraft = (item) => {
    setDraftFor({
      proposal_id: item.proposal_id,
      client_name: item.client_name || item.to,
      client_phone: threadPhones[item.proposal_id] || item.to,
      hasDm: !!threadDmFlag[item.proposal_id],
    });
  };

  const markHandled = async (pid) => {
    try {
      await api.post(`/api/admin/leads/${pid}/mark-responded`, {});
      toast.success('Handled');
      load();
    } catch { toast.error('Failed'); }
  };

  if (items === null) {
    return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading…</p>;
  }

  return (
    <div data-testid="inbox-view" className="space-y-10">
      {/* PRIORITIES — needs reply with AI Respond */}
      {priorityItems.length > 0 && (
        <div data-testid="inbox-priorities">
          <Label count={priorityItems.length} tone="text-violet-400" dotCls="bg-violet-400">Needs Your Reply</Label>
          <div className="space-y-3">
            {priorityItems.map(item => (
              <ActivityRow
                key={`pri-${item.type}-${item.id}`}
                item={item}
                needs
                expanded={expanded === `pri-${item.id}`}
                onToggle={(id) => setExpanded(expanded === `pri-${id}` ? null : `pri-${id}`)}
                onOpen={onOpenLead}
                onDraft={openDraft}
                onHandled={markHandled}
              />
            ))}
          </div>
        </div>
      )}

      {/* ALL MESSAGES — chronological, newest first */}
      <div data-testid="inbox-feed">
        <Label count={feedItems.length} tone="text-zinc-200" dotCls="bg-zinc-400">All Messages</Label>
        {feedItems.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <div className="w-14 h-14 rounded-full bg-white/[0.03] flex items-center justify-center mb-4">
              <Inbox className="w-6 h-6 text-zinc-700" strokeWidth={1.5} />
            </div>
            <p className="text-zinc-600 text-sm" style={FONT_B}>No conversations yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {feedItems.map(item => (
              <ActivityRow
                key={`feed-${item.type}-${item.id}`}
                item={item}
                needs={false}
                expanded={expanded === item.id}
                onToggle={(id) => setExpanded(expanded === id ? null : id)}
                onOpen={onOpenLead}
                onDraft={openDraft}
                onHandled={markHandled}
              />
            ))}
          </div>
        )}
      </div>

      {draftFor && (
        <DraftReply
          proposalId={draftFor.proposal_id}
          clientName={draftFor.client_name}
          clientPhone={draftFor.client_phone}
          hasDm={draftFor.hasDm}
          onClose={() => setDraftFor(null)}
          onDone={() => { setDraftFor(null); load(); }}
        />
      )}
    </div>
  );
};
